import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import useTheme from 'helpers/useTheme';
import useDocumentTitle from 'helpers/useDocumentTitle';
import LeftMenu from 'components/Navigation/LeftMenu';
import SavedPicksContent from 'components/SavedPicks/SavedPicksContent';
import ArchiveButton from 'components/SavedPicks/ArchiveButton';
import RightMenu from 'components/SavedPicks/Navigation';

import { ReactComponent as ToolTipIcon } from 'assets/icons/ToolTip.svg';
import { useGetSavedPickCount, useGetSavedPicks, useSavedPicksAdding } from 'graphQL/savedPicks/hooks';
import { CardSchemaWithIsViewed } from 'graphQL/savedPicks/models';
import { useNavBehavior } from 'components/Navigation/helpers/hooks';
import { StyledMainHeading, StyledText } from 'pages/CheckEmailPage/styled';
import { StyledNavigation } from 'components/Navigation/styled';
import { COLORS, FONTS } from 'styles/constants';
import { ReactComponent as SearchIcon } from 'assets/icons/Search.svg';
import { ReactComponent as SearchSmallIcon } from 'assets/icons/SearchSmall.svg';
import { ReactComponent as MultiSelectIcon } from 'assets/icons/MultiSelect.svg';
import { PicksFilter } from 'components/Profile/PicksFilter';
import {
  StyledPageInfoContainer,
  StyledWrap,
  StyledFloatingMenu,
  StyledFloatingButton,
  StyledSearchOverlay,
  StyledSearchControls,
  StyledSearchField,
  StyledSearchInput,
  StyledSearchIcon,
} from './styled';

// Extended type for saved picks that include filter fields from the GraphQL query
interface SavedPickCard extends CardSchemaWithIsViewed {
  topicTags?: { items?: Array<{ id: number; name: string }> };
  cardMediaTags?: { items?: Array<{ id: number; name: string }> };
}

const pageTitle = 'Saved for later';

const SavedPicks: FC = () => {
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isFadedIn, setIsFadedIn] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [selectedMediaTagNames, setSelectedMediaTagNames] = useState<string[]>([]);
  const searchTouchStartYRef = useRef<number | null>(null);

  const { items, loading, loadMore } = useGetSavedPicks({ isArchived: false });
  const { isHide, bgColor, fontColor } = useNavBehavior();

  const isDark = useTheme();
  const { totalCount } = useGetSavedPickCount(true);
  const text = "Here's your discovery queue — Check back to read, watch, listen, browse.";

  const handleFiltersChange = (filters: { topicIds?: number[]; mediaTagNames?: string[] }) => {
    setSelectedTopicIds(filters.topicIds ?? []);
    setSelectedMediaTagNames(filters.mediaTagNames ?? []);
  };

  const filteredItems = useMemo(() => {
    if (!items?.length) return items;

    const hasTopicFilter = selectedTopicIds.length > 0;
    const hasMediaFilter = selectedMediaTagNames.length > 0;
    if (!hasTopicFilter && !hasMediaFilter) return items;

    return items.filter((card) => {
      // topicTags and cardMediaTags are available on many card queries; gracefully handle absence
      const pickCard = card as SavedPickCard;
      const topicIds: number[] = pickCard?.topicTags?.items?.map((t) => t?.id).filter(Boolean) ?? [];
      const mediaNames: string[] = pickCard?.cardMediaTags?.items?.map((m) => (m?.name ?? '').trim()) ?? [];

      const topicOk = !hasTopicFilter || selectedTopicIds.some((id) => topicIds.includes(id));
      const mediaOk =
        !hasMediaFilter ||
        selectedMediaTagNames.some((name) => mediaNames.some((mn) => mn?.toLowerCase() === name?.toLowerCase()));

      return topicOk && mediaOk;
    });
  }, [items, selectedTopicIds, selectedMediaTagNames]);

  useDocumentTitle(pageTitle);

  const { cardsBaseSelection } = useSavedPicksAdding(items);

  useEffect(() => {
    // trigger fade-in once mounted; remain until user dismisses by tapping above the popup
    const t = requestAnimationFrame(() => setIsFadedIn(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowOverlay(false);
      }
    };
    let attached = false;
    if (showOverlay) {
      window.addEventListener('keydown', onKeyDown);
      attached = true;
    }
    return () => {
      if (attached) {
        window.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [showOverlay]);

  return (
    <>
      <StyledNavigation color={fontColor} isHide={isHide} bgColor={bgColor}>
        <LeftMenu />
        {!!items.length && (
          <RightMenu
            setDeleteMode={setIsDeleteMode}
            isDeleteMode={isDeleteMode}
            cardsBaseSelection={cardsBaseSelection}
          />
        )}
      </StyledNavigation>
      <StyledWrap maxWidth={528}>
        {/* Floating utility menu */}
        <StyledFloatingMenu>
          <StyledFloatingButton aria-label="Search saved picks" onClick={() => setIsSearchOpen(true)}>
            <SearchIcon />
          </StyledFloatingButton>
          <StyledFloatingButton
            isSelected={isDeleteMode}
            aria-label="Select multiple saved picks"
            onClick={() => setIsDeleteMode(!isDeleteMode)}
          >
            <MultiSelectIcon color={isDeleteMode ? COLORS.white[100] : COLORS.brown.dark[100]} />
          </StyledFloatingButton>
        </StyledFloatingMenu>
        <StyledPageInfoContainer customHeaderGap={8} isPaddingBottom={!!items?.length}>
          <StyledMainHeading isDark={isDark}>
            {pageTitle}
            <ToolTipIcon
              onClick={() => setShowOverlay(true)}
              role="button"
              aria-label="Open intro"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowOverlay(true);
                }
              }}
              height={16}
              width={16}
              style={{ marginLeft: 8, cursor: 'pointer', verticalAlign: 'middle', strokeWidth: '1px' }}
            />
          </StyledMainHeading>
          <StyledText isDark={isDark}>{text}</StyledText>
          <PicksFilter onFiltersChange={handleFiltersChange} cardsSource={items as SavedPickCard[]} />
        </StyledPageInfoContainer>
        <SavedPicksContent
          isDark={isDark}
          cards={filteredItems}
          loading={loading}
          loadMore={loadMore}
          isSelectable={isDeleteMode}
          cardsSelection={cardsBaseSelection}
        />
        {!!totalCount && <ArchiveButton isDark={isDark} isSmall={!items.length} />}
      </StyledWrap>

      {/* Intro Overlay */}
      {showOverlay && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Saved picks intro overlay"
          style={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100dvh',
            background: 'var(--transparencies-sheet-dim, rgba(0, 0, 0, 0.40))',
            transition: 'opacity 200ms ease',
            opacity: isFadedIn ? 1 : 0,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <button
            type="button"
            aria-label="Close overlay"
            onClick={() => setShowOverlay(false)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
            }}
          />
          <div
            style={{
              display: 'flex',
              width: 390,
              padding: '12px 0 34px 0',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '20px 20px 0 0',
              background: 'var(--dark-brown, #181800)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <div
              style={{
                display: 'flex',
                width: 390,
                padding: '32px 0 16px 24px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 10,
                color: COLORS.white[100],
                fontFamily: FONTS.primary,
                fontSize: 22,
                fontWeight: 400,
                lineHeight: '130%',
                fontFeatureSettings: "'liga' off, 'clig' off",
              }}
            >
              Saved
            </div>
            <div
              style={{
                color: COLORS.white[100],
                fontFamily: FONTS.secondary,
                fontSize: 14,
                fontWeight: 400,
                lineHeight: '140%',
                fontFeatureSettings: "'liga' off, 'clig' off",
                padding: '0 24px',
              }}
            >
              You&apos;ve saved these items from inside and outside of the app. Share to your profile if you liked
              something or organize into your Lists. You can also unsave items, otherwise they move to Archive after 30
              days.
            </div>
          </div>
        </div>
      )}

      {/* Search Overlay */}
      {isSearchOpen && (
        <button
          type="button"
          aria-label="Close search overlay by tapping outside"
          onClick={() => setIsSearchOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 27,
            background: 'transparent',
            border: 'none',
            zIndex: 1000,
          }}
        />
      )}
      <StyledSearchOverlay
        isOpen={isSearchOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Search saved picks"
        onTouchStart={(e) => {
          if (e.touches && e.touches.length > 0) {
            searchTouchStartYRef.current = e.touches[0].clientY;
          }
        }}
        onTouchEnd={(e) => {
          const startY = searchTouchStartYRef.current;
          const endY = e.changedTouches && e.changedTouches.length > 0 ? e.changedTouches[0].clientY : null;
          if (startY != null && endY != null && endY - startY > 30) {
            setIsSearchOpen(false);
          }
          searchTouchStartYRef.current = null;
        }}
      >
        <StyledSearchControls>
          <StyledSearchField>
            <StyledSearchIcon>
              <SearchSmallIcon />
            </StyledSearchIcon>
            <StyledSearchInput placeholder="Keywords" />
          </StyledSearchField>
        </StyledSearchControls>
      </StyledSearchOverlay>
    </>
  );
};

export default SavedPicks;
